/**
 * Search, assignment, lifecycle, and ordering controls for the notes inbox.
 *
 * The parent page owns state and translates these controls into server queries.
 */

import { Search } from "lucide-react";
import type { NoteAssigneeResponse } from "../../api/clinicalTrialsApiTypes";
import BadgeDropdown from "../../components/badgeDropdown/badgeDropdown";
import type { BadgeWithIconProps } from "../../data-model/badgeWithIconProps";
import type { NotesFilters } from "../../types/notes";

interface NotesFiltersBarProps {
  filters: NotesFilters;
  assignees: NoteAssigneeResponse[];
  onChange: (filters: NotesFilters) => void;
}

interface NotesFilterDropdownProps {
  ariaLabel: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}

/** Neutral BadgeDropdown adapter used by each Notes toolbar selector. */
function NotesFilterDropdown({
  ariaLabel,
  value,
  options,
  onChange,
}: NotesFilterDropdownProps) {
  const badgeList: BadgeWithIconProps[] = options.map((option) => ({
    id: option.value,
    text: option.label,
    textColor: "#0F0F0F",
    bgColor: "#F5F5F5",
  }));
  const selectedBadge =
    badgeList.find((option) => option.id === value) ?? badgeList[0] ?? null;

  return (
    <div aria-label={ariaLabel}>
      <BadgeDropdown
        selectedBadge={selectedBadge}
        badgeList={badgeList}
        onClick={onChange}
        shouldUsePortal
      />
    </div>
  );
}

/**
 * Render server-backed notes filter controls.
 *
 * @param props - Current filters, assignment directory, and update callback
 * @returns Notes inbox toolbar
 */
export function NotesFiltersBar({
  filters,
  assignees,
  onChange,
}: NotesFiltersBarProps) {
  const toggle = (key: "unreadOnly" | "assignedToMe" | "overdueOnly") =>
    onChange({ ...filters, [key]: !filters[key] });
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <label className="flex min-w-[280px] flex-1 items-center gap-2 rounded-md border border-uiGray-300 bg-white px-3 py-2">
        <Search size={15} className="text-primaryGray-9" />
        <input
          type="search"
          aria-label="Search notes"
          placeholder="Search notes, patients, MRNs, creators, or assignees"
          value={filters.query}
          onChange={(event) => onChange({ ...filters, query: event.target.value })}
          className="w-full bg-transparent text-sm outline-none"
        />
      </label>
      {[
        ["unreadOnly", "Unread"],
        ["assignedToMe", "Assigned to me"],
        ["overdueOnly", "Overdue"],
      ].map(([key, label]) => (
        <button
          key={key}
          type="button"
          aria-pressed={filters[key as keyof NotesFilters] === true}
          onClick={() =>
            toggle(key as "unreadOnly" | "assignedToMe" | "overdueOnly")
          }
          className={`rounded-md border px-3 py-2 text-xs font-medium ${
            filters[key as keyof NotesFilters]
              ? "border-statusBlue-text bg-statusBlue-bg text-statusBlue-darkText"
              : "border-uiGray-300 bg-white text-primaryGray-7"
          }`}
        >
          {label}
        </button>
      ))}
      <NotesFilterDropdown
        ariaLabel="Filter by assignee"
        value={filters.assigneeId}
        options={[
          { value: "", label: "All assignees" },
          ...assignees.map((assignee) => ({
            value: assignee.user_id,
            label: assignee.display_name || assignee.email || "",
          })),
        ]}
        onChange={(assigneeId) => onChange({ ...filters, assigneeId })}
      />
      <NotesFilterDropdown
        ariaLabel="Filter by status"
        value={filters.status}
        options={[
          { value: "all", label: "All statuses" },
          { value: "open", label: "Open" },
          { value: "resolved", label: "Resolved" },
        ]}
        onChange={(status) =>
          onChange({
            ...filters,
            status: status as NotesFilters["status"],
          })
        }
      />
      <NotesFilterDropdown
        ariaLabel="Sort notes"
        value={`${filters.orderBy}:${filters.orderDirection}`}
        options={[
          { value: "created_at:desc", label: "Newest created" },
          { value: "updated_at:desc", label: "Latest activity" },
          { value: "due_date:asc", label: "Due date" },
        ]}
        onChange={(value) => {
          const [orderBy, orderDirection] = value.split(":");
          onChange({
            ...filters,
            orderBy: orderBy as NotesFilters["orderBy"],
            orderDirection: orderDirection as NotesFilters["orderDirection"],
          });
        }}
      />
    </div>
  );
}
